import React from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Line,
} from "recharts";
import {
  Droplets,
  Activity,
  Zap,
  DollarSign,
  BarChart3,
  TrendingDown,
  ShieldCheck,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  LayoutDashboard,
} from "lucide-react";
import { useSelector } from "react-redux";

// --- Executive Summary Mock Data ---
const SUMMARY_DATA = {
  kpis: {
    waterProduced: "850.4", // k m³
    waterBilled: "612.2", // k m³
    nrwPercentage: "28", // %
    energyCost: "42.5", // k
    assetAvailability: "94.2", // %
  },
  // Data for 'Water Production & Billing' Chart
  monthlyTrend: [
    { month: "Jan", produced: 800, billed: 580 },
    { month: "Feb", produced: 750, billed: 540 },
    { month: "Mar", produced: 850, billed: 612 },
    { month: "Apr", produced: 820, billed: 590 },
    { month: "May", produced: 900, billed: 640 },
  ],
  // Data for 'NRW Percentage Trend' Chart
  nrwTrend: [
    { month: "Jan", nrw: 32 },
    { month: "Feb", nrw: 30 },
    { month: "Mar", nrw: 28 },
    { month: "Apr", nrw: 29 },
    { month: "May", nrw: 27 },
  ],
};

const ManagementSummary = () => {
  const themeMode = useSelector((state) => state.theme?.mode);
  const cardBg = themeMode ? "bg-slate-900/80" : "bg-white";
  const textColor = themeMode ? "dark_text-color" : "text-primary";
  const bgColor = themeMode ? "bg_light_main" : "bg-white";
  const borderCol = themeMode ? "border_them_2" : "border-primary";

  return (
    <div className={`w-full ${bgColor} ${textColor} h-full`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderCol} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColor} items-center  gap-x-2`}
        >
          <p>
            <LayoutDashboard size={16} />
          </p>
          <p className="font-semibold">AssetFlow Report</p>
        </div>
      </div>

      <div className=" h-[calc(100vh-50px)] pb-10 pt-5 sidebar_scroll overflow-y-auto sidebar_scroll">
        <div className="px-3 md:px-5 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-10">
            <ExecutiveKPICard
              label="Water Produced"
              value={SUMMARY_DATA.kpis.waterProduced}
              unit="k m³"
              icon={<Droplets size={22} />}
              color="bg-blue-600"
              cardBg={cardBg}
              borderCol={borderCol}
            />
            <ExecutiveKPICard
              label="Water Billed"
              value={SUMMARY_DATA.kpis.waterBilled}
              unit="k m³"
              icon={<ShieldCheck size={22} />}
              color="bg-green-600"
              cardBg={cardBg}
              borderCol={borderCol}
            />
            <ExecutiveKPICard
              label="NRW %"
              value={SUMMARY_DATA.kpis.nrwPercentage}
              unit="%"
              icon={<TrendingDown size={22} />}
              color="bg-yellow-500"
              cardBg={cardBg}
              borderCol={borderCol}
              trend="-4%"
              isGood={true}
            />
            <ExecutiveKPICard
              label="Energy Cost"
              value={SUMMARY_DATA.kpis.energyCost}
              unit="k ₹"
              icon={<Zap size={22} />}
              color="bg-orange-600"
              cardBg={cardBg}
              borderCol={borderCol}
            />
            <ExecutiveKPICard
              label="Asset Availability"
              value={SUMMARY_DATA.kpis.assetAvailability}
              unit="%"
              icon={<Activity size={22} />}
              color="bg-emerald-600"
              cardBg={cardBg}
              borderCol={borderCol}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* --- Report: Water Production & Billing Trend --- */}
            <div
              className={`lg:col-span-8 ${cardBg} border ${borderCol} rounded-3xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="font-semibold text-base tracking-tight italic">
                    Water Production & Billing
                  </h3>
                  <p className="text-xs opacity-50  font-semibold tracking-tighter">
                    Daily volume tracking (m³)
                  </p>
                </div>
                <div className="flex gap-6 text-[10px] font-black uppercase opacity-60">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded bg-blue-600" /> Produced
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded bg-green-600" /> Billed
                  </span>
                </div>
              </div>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={SUMMARY_DATA.monthlyTrend} barGap={8}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="month"
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                      unit="k"
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: "15px",
                        border: "none",
                        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar
                      name="Produced"
                      dataKey="produced"
                      fill="#2563eb"
                      radius={[6, 6, 0, 0]}
                      barSize={25}
                    />
                    <Bar
                      name="Billed"
                      dataKey="billed"
                      fill="#16a34a"
                      radius={[6, 6, 0, 0]}
                      barSize={25}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* --- Report: NRW Percentage Trend --- */}
            <div
              className={`lg:col-span-4 ${cardBg} border ${borderCol} rounded-3xl p-3 shadow-sm`}
            >
              <div className="mb-8">
                <h3 className="font-semibold text-base tracking-tight italic">
                  NRW Percentage Trend
                </h3>
                <p className="text-xs opacity-50  font-semibold tracking-tighter">
                  Non-revenue water over time
                </p>
              </div>
              <div className="h-[250px] mb-8">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={SUMMARY_DATA.nrwTrend}>
                    <defs>
                      <linearGradient id="colorNrw" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#eab308"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#eab308"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="nrw"
                      stroke="#eab308"
                      fillOpacity={1}
                      fill="url(#colorNrw)"
                      strokeWidth={4}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="p-4 bg-yellow-500/5 rounded-2xl border border-yellow-500/10">
                <p className="text-[10px] font-black uppercase text-yellow-600 mb-1">
                  Efficiency Insight
                </p>
                <p className="text-xs font-semibold leading-relaxed opacity-80">
                  Target NRW is 25%. Current trend shows a{" "}
                  <span className="text-green-600">3.2% reduction</span> over
                  the last quarter due to leak detection in Narwal subdivision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Reusable Sub-Component for Executive KPIs ---
const ExecutiveKPICard = ({
  label,
  value,
  unit,
  icon,
  color,
  trend,
  isGood,
  cardBg,
  borderCol,
}) => (
  <div
    className={`${cardBg} border ${borderCol} p-6 rounded-2xl shadow-sm relative overflow-hidden group hover:scale-[1.02] transition-transform`}
  >
    <div
      className={`absolute top-0 right-0 w-20 h-20 ${color} opacity-[0.03] rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700`}
    ></div>
    <div className="flex justify-between items-start mb-6 relative z-10">
      <div
        className={`p-3 rounded-2xl text-white ${color} shadow-lg shadow-current/20`}
      >
        {icon}
      </div>
      {trend && (
        <span
          className={`text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1 ${
            isGood
              ? "bg-green-500/10 text-green-600"
              : "bg-red-500/10 text-red-600"
          }`}
        >
          {isGood ? <ArrowDownRight size={12} /> : <ArrowUpRight size={12} />}{" "}
          {trend}
        </span>
      )}
    </div>
    <div className="flex items-baseline gap-1 mb-1 relative z-10">
      <p className="text-xl font-black tracking-tighter leading-none">
        {value}
      </p>
      <p className="text-xs font-black opacity-30 uppercase">{unit}</p>
    </div>
    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest relative z-10 leading-none">
      {label}
    </p>
  </div>
);

export default ManagementSummary;
